import React, { useContext, useEffect, useState } from 'react';
import format from 'date-fns/format';
import classNames from 'classnames';

import useApi from 'common/useApi';
import UiContext from 'common/UiContext/UiContext';
import formatBtc from 'common/formatBtc';
import ILedgerEntry from 'common/types/ILedgerEntry';

import DataTable from 'components/DataTable';
import DataTablePagination from 'components/DataTablePagination';

// Append-only audit trail of every balance-affecting event. The ledger mirrors
// Miningcore's pendingBalance via deltas, so each row explains a change and
// `balance_after` is the running balance at that point.
const humanize = (value: string) =>
  value
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase());

const Ledger = () => {
  const { addToast } = useContext(UiContext);
  const [page, setPage] = useState(1);

  const {
    data: ledger,
    isError,
    isLoading,
    isFetching,
  } = useApi('getList', '/api/ledger', { page });

  const headers = [
    'Date',
    'Type',
    'Amount (BTC)',
    'Balance (BTC)',
    'Source',
    'Reference',
  ];

  const ledgerList = () => ledger?.results.map(({
    created_at,
    entry_type,
    amount,
    balance_after,
    source,
    reference_type,
    reference_id,
  }: ILedgerEntry) => [
    format(new Date(created_at), 'yyyy-MM-dd HH:mm:ss'),
    humanize(entry_type),
    formatBtc(amount),
    formatBtc(balance_after),
    humanize(source),
    reference_type ? `${reference_type} #${reference_id}` : '—',
  ]);

  useEffect(() => {
    if (isError) {
      addToast!('Error loading data. Please, retry');
    }
  }, [isError]);

  if (!ledger) {
    return null;
  }

  return (
    <>
      <div
        className={classNames({
          box: true,
          isLoading: isLoading || isFetching,
          table: true,
        })}
      >
        <DataTable isLoading={isLoading || isFetching} headers={headers} content={ledgerList() ?? []} />
        <DataTablePagination
          page={page}
          totalItems={ledger?.count ?? 0}
          setPage={setPage}
        />
      </div>
    </>
  );
};

export default Ledger;
